import { Link } from 'react-router-dom'
import { EtiquetaDePrioridade, EtiquetaDeRisco, EtiquetaDeStatus } from '../../components/Etiqueta'
import { TEXTOS } from '../../lib/glossario'
import { data } from '../../lib/datas'
import type { ItemDaPauta } from '../../types/cab'
import type { NivelDeRisco } from '../../types/gmud'

/** Cores de semáforo, as mesmas do painel de apoio dentro da solicitação. */
const COR_DA_RECOMENDACAO: Record<string, string> = {
  RECOMENDA_APROVAR: 'bg-green-100 text-green-900',
  APROVAR_COM_RESSALVAS: 'bg-amber-100 text-amber-900',
  RECOMENDA_REPROVAR: 'bg-red-100 text-red-900',
}

/** Quantos dias inteiros a solicitação está esperando. */
function diasDeEspera(criadoEm: string): number {
  const ms = Date.now() - new Date(criadoEm).getTime()
  return Math.max(0, Math.floor(ms / 86_400_000))
}

/**
 * Uma solicitação na pauta, com o material de apoio ao lado.
 *
 * **A recomendação aparece grande e mesmo assim não é a decisão** — e a tensão é deliberada. Ela
 * está em destaque porque é o que faz o comitê saber por onde começar; e a linha abaixo do bloco
 * repete, em toda a tela, que quem aprova é o presidente. Esconder a recomendação para evitar o
 * mal-entendido devolveria o problema anterior: o parecer existia, custava modelo, e ninguém o via
 * sem abrir solicitação por solicitação.
 *
 * **"Sem parecer" é escrito, não omitido.** Célula vazia se lê como "nada a apontar". Aqui, a
 * ausência é a informação: ninguém pediu a análise.
 *
 * @param props o item e se a IA está desligada no ambiente
 * @returns a linha da pauta
 */
export function LinhaDaPauta({
  item,
  iaDesligada,
  revisaoDisponivel = true,
}: {
  item: ItemDaPauta
  iaDesligada: boolean
  /** Com a integração desligada, "nenhum PR" seria mentira por omissão. */
  revisaoDisponivel?: boolean
}) {
  const espera = diasDeEspera(item.criadoEm)

  return (
    <li className="cartao p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/gmuds/${item.id}`} className="font-mono text-sm font-semibold text-niteroi-800 hover:underline">
              {item.codigo}
            </Link>
            <EtiquetaDeStatus status={item.status} rotulo={item.statusRotulo} />
            {/* A prioridade estava em toda tela do comitê menos nesta — a que decide o que discutir
                primeiro. Emergencial é o que justifica reunião extraordinária. */}
            {item.prioridadeCodigo && <EtiquetaDePrioridade prioridade={item.prioridadeCodigo} />}
            <EtiquetaDeRisco risco={item.riscoNivel as NivelDeRisco | null} />
          </div>
          <p className="mt-1 text-sm text-slate-700">
            {[item.sistema, item.finalidade].filter(Boolean).join(' · ') || '—'}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {item.solicitante ?? '—'} · {TEXTOS.cab.esperaHa(espera)}
            {item.chamadoNumero && ` · chamado ${item.chamadoNumero}`}
            {item.implementacaoPrevista && ` · implantação prevista ${data(item.implementacaoPrevista)}`}
          </p>
        </div>

        <Link
          to={`/gmuds/${item.id}`}
          className="shrink-0 rounded-lg bg-niteroi-700 px-3.5 py-2 text-sm font-medium text-white hover:bg-niteroi-800"
        >
          {item.status === 'AGUARDANDO_AJUSTES' ? TEXTOS.cab.abrirSemDecidir : TEXTOS.cab.abrir}
        </Link>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        {item.recomendacaoDaIa ? (
          <span
            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
              COR_DA_RECOMENDACAO[item.recomendacaoDaIa] ?? 'bg-slate-100 text-slate-800'
            }`}
          >
            {TEXTOS.apoioAoComite.recomendacao[
              item.recomendacaoDaIa as keyof typeof TEXTOS.apoioAoComite.recomendacao
            ] ?? item.recomendacaoDaIa}
          </span>
        ) : (
          <span
            title={iaDesligada ? TEXTOS.cab.iaDesligada : TEXTOS.cab.semParecerAjuda}
            className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600"
          >
            {TEXTOS.cab.semParecer}
          </span>
        )}

        {item.ressalvasDaIa > 0 && (
          <span className="text-xs text-amber-800">
            {item.ressalvasDaIa} {item.ressalvasDaIa === 1 ? 'ressalva' : 'ressalvas'} a conferir
          </span>
        )}

        {revisaoDisponivel && (
          <span className="text-xs text-slate-500">{TEXTOS.cab.prs(item.prsVinculados, item.prsRevisados)}</span>
        )}

        {item.algumPrPedeMudanca && <span className="text-xs font-medium text-red-800">{TEXTOS.cab.pedeMudanca}</span>}
      </div>

      {item.resumoDaIa && <p className="mt-2 max-w-prose text-xs text-slate-600">{item.resumoDaIa}</p>}
    </li>
  )
}
